import { useCallback, useEffect, useRef, useState } from 'react';

import uuid from 'uuid';

import {
	AnalyticsEventSource,
	useAnalytics,
	usePrincipalUser,
	useSite,
} from '@atlassian/ptc-common';
import { teamsClient } from '@atlassian/teams-client';
import { useServiceLight } from '@atlassian/teams-common/use-service';

import {
	type CollaborationGraphContainerFormat,
	toContainersViewFormat,
} from './transformToContainersViewFormat';
import { type ContainersQueryData, type ContainersQueryProps } from './types';

interface QueryData {
	Containers: {
		collaborationGraphEntities: CollaborationGraphContainerFormat[];
	};
}

const getCurrentTime = () => (window && window.performance ? window.performance.now() : Date.now());

function useContainersQuery(props: ContainersQueryProps): ContainersQueryData {
	const [{ cloudId }] = useSite();
	const [{ principalUserId }] = usePrincipalUser();
	const analytics = useAnalytics();

	const [sessionId] = useState(uuid());
	const [startTime, setStartTime] = useState(0);
	const userId = props.userId || principalUserId;

	const request = useCallback(() => {
		return teamsClient.getUserContainers({
			siteId: cloudId,
			userId,
			principalId: principalUserId,
			maxNumberOfResults: props.maxNumberOfCards,
			sessionId,
		});
	}, [cloudId, principalUserId, props.maxNumberOfCards, sessionId, userId]);

	const { fetchData, loading, data, error } = useServiceLight(request);
	const loadingRef = useRef<boolean>(loading);

	useEffect(() => {
		setStartTime(getCurrentTime());
	}, []);

	useEffect(() => {
		if (principalUserId) {
			fetchData();
		}
	}, [fetchData, principalUserId]);

	const triggerAnalyticsEvent = useCallback(
		(elapsedTimeMs: number, data: QueryData) => {
			const containers = (data.Containers && data.Containers.collaborationGraphEntities) || [];
			const ids = containers.map(({ id }) => id);
			analytics.pushOperationalEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'collaboration-graph',
				attributes: {
					resultLength: ids.length,
					sessionId,
					resultIds: ids,
					endpoint: 'userToContainer',
					userId,
					elapsedTimeMs,
				},
				action: 'requested',
			});
		},
		[userId, analytics, sessionId],
	);

	if (loadingRef.current !== loading) {
		loadingRef.current = loading;
		if (!loading) {
			const end = getCurrentTime();
			const diff = end - startTime;
			triggerAnalyticsEvent(diff, { Containers: data || { collaborationGraphEntities: [] } });
		}
	}

	return {
		data: data ? toContainersViewFormat(data.collaborationGraphEntities) : [],
		loading,
		hasError: !!error,
		error,
	};
}

export default useContainersQuery;
